import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type DragEndEvent as ReactDragEndEvent,
  type DragMoveEvent as ReactDragMoveEvent,
  type DragOverEvent as ReactDragOverEvent,
  type DragStartEvent as ReactDragStartEvent,
  useDragDropMonitor,
} from '@dnd-kit/react';
import { arrayMove } from '@dnd-kit/sortable';
import { v7 as uuidv7 } from 'uuid';

import { EDITOR_COLLECTION_ITEM_DRAG_TYPE } from '@/components/blocks/editor/collection/collection-dnd';
import type { CollectionItem } from '@/lib/blocks/collection/blocks';
import { getDocument } from '@/lib/utils';
import {
  type CollectionReorderPointerPreference,
  readClientPointFromDragNativeEvent,
  resolvePointerEdgeWithinRect,
} from '../dnd/collection/resolve-collection-item-hover-edge';
import { resolveCollectionReorderTargetIndex } from '../dnd/collection/resolve-collection-reorder-target-index';
import {
  type DropValidationEventInput,
  getEventActivator,
  getEventActiveData,
  getEventActiveId,
  getEventActiveTranslatedRect,
  getEventOver,
  unwrapDndKitData,
} from '../dnd/resolution/event-adapters';
import type { CollectionReorderIndicator } from './collection-reorder-visual-context';

const DUP_SUFFIX_RE = /:dup:\d+$/;
function resolveRealItemId(sortableId: string): string {
  return sortableId.replace(DUP_SUFFIX_RE, '');
}

interface UseCollectionDragReorderOptions {
  items: CollectionItem[];
  collectionStableId: string;
  reorderPointerPreference: CollectionReorderPointerPreference;
  saveItems: (nextItems: CollectionItem[]) => void;
  previewItems?: (nextItems: CollectionItem[]) => void;
  releaseCollectionBlockSelection: () => void;
  onReorderActiveChange?: (active: boolean) => void;
  collectionBlockActive?: boolean;
}

export function useCollectionDragReorder({
  items,
  collectionStableId,
  reorderPointerPreference,
  saveItems,
  previewItems,
  releaseCollectionBlockSelection,
  onReorderActiveChange,
  collectionBlockActive = false,
}: UseCollectionDragReorderOptions) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [reorderVisual, setReorderVisual] = useState<{
    reorderDragActive: boolean;
    indicator: CollectionReorderIndicator | null;
  }>({ reorderDragActive: false, indicator: null });
  const latestReorderIndicatorRef = useRef<CollectionReorderIndicator | null>(null);

  const onReorderActiveChangeRef = useRef(onReorderActiveChange);
  onReorderActiveChangeRef.current = onReorderActiveChange;
  const prevReorderDragActiveRef = useRef(false);
  useEffect(() => {
    const next = reorderVisual.reorderDragActive;
    if (next !== prevReorderDragActiveRef.current) {
      prevReorderDragActiveRef.current = next;
      onReorderActiveChangeRef.current?.(next);
    }
  }, [reorderVisual.reorderDragActive]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (selectedItemId && !items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(null);
    }
  }, [items, selectedItemId]);

  useEffect(() => {
    if (collectionBlockActive) {
      setSelectedItemId(null);
    }
  }, [collectionBlockActive]);

  const saveItemsRef = useRef(saveItems);
  saveItemsRef.current = saveItems;

  const previewItemsRef = useRef(previewItems);
  previewItemsRef.current = previewItems;

  const releaseCollectionBlockSelectionRef = useRef(releaseCollectionBlockSelection);
  releaseCollectionBlockSelectionRef.current = releaseCollectionBlockSelection;

  const updateReorderIndicatorFromEvent = useCallback(
    (event: DropValidationEventInput) => {
      const currentItems = itemsRef.current;
      const overSnap = getEventOver(event);
      const overId = overSnap?.id;
      const realOverId = overId ? resolveRealItemId(String(overId)) : undefined;
      if (!realOverId || !currentItems.some((item) => item.id === realOverId)) {
        latestReorderIndicatorRef.current = null;
        setReorderVisual((prev) => ({ ...prev, indicator: null }));
        return;
      }

      const doc = getDocument();
      const el = doc?.querySelector(`[data-collection-sortable-id="${String(overId)}"]`);
      const rect = el?.getBoundingClientRect();
      const translated = getEventActiveTranslatedRect(event);
      const pt =
        readClientPointFromDragNativeEvent(getEventActivator(event) ?? undefined) ??
        (rect && translated
          ? {
              x: translated.left + translated.width / 2,
              y: translated.top + translated.height / 2,
            }
          : null);
      if (!rect || !pt) {
        latestReorderIndicatorRef.current = null;
        setReorderVisual((prev) => ({ ...prev, indicator: null }));
        return;
      }

      const edge = resolvePointerEdgeWithinRect(pt.x, pt.y, rect, reorderPointerPreference);
      const indicator: CollectionReorderIndicator = {
        targetItemId: String(overId),
        edge,
      };
      latestReorderIndicatorRef.current = indicator;
      setReorderVisual({ reorderDragActive: true, indicator });
    },
    [reorderPointerPreference],
  );

  const handleCollectionDragMonitorEnd = useCallback((event: ReactDragEndEvent) => {
    const indicatorSnapshot = latestReorderIndicatorRef.current;
    latestReorderIndicatorRef.current = null;
    setReorderVisual({ reorderDragActive: false, indicator: null });

    const currentItems = itemsRef.current;
    const rawMovedId = getEventActiveId(event);
    const movedId = resolveRealItemId(rawMovedId);
    const finish = () => {
      setSelectedItemId(movedId);
      releaseCollectionBlockSelectionRef.current();
    };

    const overSnap = getEventOver(event);
    const rawOverId = overSnap ? String(overSnap.id) : undefined;
    const realOverId = rawOverId ? resolveRealItemId(rawOverId) : undefined;
    if (!overSnap || movedId === realOverId) {
      finish();
      return;
    }
    const oldIndex = currentItems.findIndex((item) => item.id === movedId);
    const overIndex = currentItems.findIndex((item) => item.id === realOverId);
    if (oldIndex === -1 || overIndex === -1) {
      finish();
      return;
    }

    const pointerLike =
      readClientPointFromDragNativeEvent(getEventActivator(event) ?? undefined) !== null;
    const useEdge =
      pointerLike && indicatorSnapshot && String(indicatorSnapshot.targetItemId) === rawOverId;
    const newIndex = useEdge
      ? resolveCollectionReorderTargetIndex({
          oldIndex,
          overIndex,
          edge: indicatorSnapshot.edge,
        })
      : overIndex;

    saveItemsRef.current(arrayMove(currentItems, oldIndex, newIndex));
    finish();
  }, []);

  const isOwnCollectionDrag = useCallback(
    (event: DropValidationEventInput) => {
      const data = getEventActiveData<unknown>(event);
      const payload = unwrapDndKitData<Record<string, unknown>>(data);
      if (payload?.type !== EDITOR_COLLECTION_ITEM_DRAG_TYPE) return false;
      return payload.collectionStableId === collectionStableId;
    },
    [collectionStableId],
  );

  const guarded = useCallback(
    <E>(handler: (event: E) => void) =>
      (event: E) => {
        if (isOwnCollectionDrag(event as DropValidationEventInput)) handler(event);
      },
    [isOwnCollectionDrag],
  );

  const dragMonitorHandlers = useMemo(
    () => ({
      onDragStart: guarded((_event: ReactDragStartEvent) => {
        latestReorderIndicatorRef.current = null;
        setReorderVisual({ reorderDragActive: true, indicator: null });
      }),
      onDragOver: guarded((event: ReactDragOverEvent) => {
        updateReorderIndicatorFromEvent(event);
      }),
      onDragMove: guarded((event: ReactDragMoveEvent) => {
        updateReorderIndicatorFromEvent(event);
      }),
      onDragEnd: guarded((event: ReactDragEndEvent) => {
        handleCollectionDragMonitorEnd(event);
      }),
    }),
    [guarded, handleCollectionDragMonitorEnd, updateReorderIndicatorFromEvent],
  );

  useDragDropMonitor(dragMonitorHandlers);

  const handleUpdate = useCallback((updated: CollectionItem, commit = true) => {
    const nextItems = itemsRef.current.map((item) => (item.id === updated.id ? updated : item));
    if (commit) {
      saveItemsRef.current(nextItems);
    } else {
      previewItemsRef.current?.(nextItems);
    }
  }, []);

  const handleDelete = useCallback((itemId: string) => {
    saveItemsRef.current(itemsRef.current.filter((item) => item.id !== itemId));
  }, []);

  const handleDuplicate = useCallback((itemId: string) => {
    const source = itemsRef.current.find((item) => item.id === itemId);
    if (!source) return;
    const index = itemsRef.current.findIndex((item) => item.id === itemId);
    const cloned: CollectionItem = { ...source, id: uuidv7() };
    const next = [...itemsRef.current];
    next.splice(index + 1, 0, cloned);
    saveItemsRef.current(next);
  }, []);

  const handleSelectionChange = useCallback(
    (id: string, selected: boolean) => {
      setSelectedItemId((prev) => {
        if (selected) {
          return id;
        }
        return prev === id ? null : prev;
      });
      if (selected) {
        releaseCollectionBlockSelection();
      }
    },
    [releaseCollectionBlockSelection],
  );

  return {
    reorderVisual,
    selectedItemId,
    setSelectedItemId,
    handleUpdate,
    handleDelete,
    handleDuplicate,
    handleSelectionChange,
  };
}
